import React, { useEffect, useId, useRef, useState } from 'react';
import { Copy, MoreHorizontal, Pencil, Plus, Power, RotateCcw, Store, Trash2 } from 'lucide-react';
import { t } from '../i18n';
import './MapNoteActions.css';

export default function MapNoteActions({
  onAdd,
  onReview,
  onEdit,
  onCopy,
  onDelete,
  onPublish,
  onToggleState,
  renderMenuItems,
  copySuccess = false,
  noteEnabled = true,
  stateSaving = false,
  disabled = false,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();
  const triggerId = useId();
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const menuItems = () => Array.from(menuRef.current?.querySelectorAll('button:not(:disabled)') || []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    menuRef.current?.querySelector('button:not(:disabled)')?.focus();
    const dismissOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) setMenuOpen(false);
    };
    document.addEventListener('pointerdown', dismissOutside, true);
    return () => document.removeEventListener('pointerdown', dismissOutside, true);
  }, [menuOpen]);

  const closeMenu = () => {
    setMenuOpen(false);
    triggerRef.current?.focus();
  };

  const handleMenuKeyDown = (event) => {
    if (event.key === 'Escape' && menuOpen) {
      event.preventDefault();
      event.stopPropagation();
      closeMenu();
      return;
    }

    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    if (!menuOpen) {
      setMenuOpen(true);
      return;
    }

    const items = menuItems();
    if (!items.length) return;
    const currentIndex = items.indexOf(document.activeElement);
    let nextIndex = 0;
    if (event.key === 'End') nextIndex = items.length - 1;
    else if (event.key === 'ArrowUp') nextIndex = currentIndex <= 0 ? items.length - 1 : currentIndex - 1;
    else if (event.key === 'ArrowDown') nextIndex = currentIndex < 0 || currentIndex === items.length - 1 ? 0 : currentIndex + 1;
    items[nextIndex]?.focus();
  };

  return (
    <div className="map-note-actions" role="group" aria-label={t('interface.map.actions')}>
      {!onReview && <button type="button" className="map-note-action" onClick={onEdit || onAdd} disabled={disabled} aria-label={onEdit ? t('notesUnified.edit') : undefined} title={onEdit ? t('notesUnified.edit') : t('interface.map.addTitle')}>
        {onEdit ? <Pencil size={17} aria-hidden="true" /> : <Plus size={17} aria-hidden="true" />}
        <span>{t(onEdit ? 'notesUnified.edit' : 'interface.map.add')}</span>
      </button>}
      {onReview && <button type="button" className="map-note-action" onClick={onReview} disabled={disabled} aria-label={t('reviewLevelUp.noteAction')} title={t('reviewLevelUp.noteAction')}>
        <RotateCcw size={17} aria-hidden="true" />
      </button>}
      <div
        ref={containerRef}
        className="map-note-more"
        onBlur={(event) => {
          // Safari may focus a tabindex ancestor (AppMain), or nothing, when a
          // menu button is tapped. Neither means the pointer left the menu.
          // Keep it mounted until click; outside taps have their own handler.
          const nextFocus = event.relatedTarget;
          if (nextFocus
            && !event.currentTarget.contains(nextFocus)
            && !nextFocus.contains?.(event.currentTarget)) {
            setMenuOpen(false);
          }
        }}
        onKeyDown={handleMenuKeyDown}
      >
        <button
          ref={triggerRef}
          id={triggerId}
          type="button"
          className="map-note-action map-note-action-more"
          disabled={disabled}
          aria-label={t('interface.map.moreActions')}
          title={t('interface.map.moreActions')}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          aria-controls={menuOpen ? menuId : undefined}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <MoreHorizontal size={21} aria-hidden="true" />
        </button>
        {menuOpen && (
          <div ref={menuRef} id={menuId} role="menu" aria-labelledby={triggerId} className="map-note-menu">
            <button
              type="button"
              role="menuitem"
              disabled={disabled || stateSaving}
              className={`map-note-menu-state ${noteEnabled ? 'is-enabled' : 'is-disabled'}`}
              onClick={onToggleState}
            >
              <Power size={16} strokeWidth={2.4} aria-hidden="true" />
              <span>{t(stateSaving ? 'notes.savingState' : noteEnabled ? 'notes.disable' : 'notes.enable')}</span>
            </button>
            <button
              type="button"
              role="menuitem"
              disabled={disabled}
              onClick={onCopy}
              title={t('interface.map.copyTitle')}
            >
              <Copy size={16} aria-hidden="true" />
              <span aria-live="polite">{t(copySuccess ? 'interface.map.copied' : 'interface.map.copy')}</span>
            </button>
            {onPublish && (
              <button
                type="button"
                role="menuitem"
                disabled={disabled}
                onClick={() => { closeMenu(); onPublish(); }}
              >
                <Store size={16} aria-hidden="true" />
                <span>{t('market.publish')}</span>
              </button>
            )}
            {renderMenuItems?.(closeMenu)}
            <button
              type="button"
              role="menuitem"
              disabled={disabled}
              className="is-danger"
              onClick={() => { closeMenu(); onDelete?.(); }}
              title={t('interface.map.deleteTitle')}
            >
              <Trash2 size={16} aria-hidden="true" />
              <span>{t('interface.map.delete')}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
